import React, { useState } from 'react';
import KeepsakeShelf from './KeepsakeShelf';
import PortedWindowCard from './PortedWindowCard';

// 🪟🏮 PortedRail — "The Hearthside Rail" (Nina 08-28, design-panel winner).
// Fills the ported-space RIGHT COLUMN (desktop only), which otherwise renders
// empty (every legacy child is !isPortedSpace-gated). It is the resident's side
// of the room: they are present and making things, not dead bokeh.
//
// Top → bottom: (1) presence + tempo — a 30px portrait DOT + the live "right
// now" line, folded above the honest turn-clock (elapsed-since-a-real-event + a
// STATIC cadence note, NEVER a ticking countdown — the fake-heartbeat the arc
// bans). The masthead hero owns the big portrait + name + DAY N now (Nina 08-29
// de-dup); the dot re-anchors the face when that hero scrolls out of view;
// (3) the Mind card RETIRED 09-25 (Nina: "What X sees" goes; the journal
// replaces this whole rail behind its own flag); (4) the keepsake shelf with
// a "draw them something" button (the making loop: draw → keepsake → shelf).
//
// ZERO LLM, zero queries — every block reads state the page already holds
// (portedMindProps + keepsakeActions). Desktop-only: mobile keeps the drawer
// tabs. Windows stack + "while you were out" slot are the fast-follows.

const SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';

const Block = ({ children, style }) => (
  <div style={{
    boxSizing: 'border-box', borderRadius: 12, padding: '13px 14px',
    border: '1px solid rgba(255,236,210,0.12)',
    background: 'linear-gradient(168deg, rgba(34,23,41,0.55), rgba(23,16,23,0.55))',
    ...style,
  }}>{children}</div>
);

const PortedRail = ({
  gameId = null,
  residentName = null,
  residentImage = null,
  residentStatus = null,
  // dayN, residentModel, ownerName, viewerIsOwner, pokesQueued, showThoughts:
  // the parent spreads portedMindProps; unused here since the Mind card left
  // the rail (09-25) and the masthead hero owns "day N together" (08-29).
  lastTurnAgo = null,     // coarse "a minute ago" string, or null when unknown
  keepsakeActions = [],
  windows = [],           // [{ actionId, title, body }] resident-authored windows
  windowAdds = {},        // { [actionId]: [{ line, at }] } collaborative lines
  canInteract = false,    // owner seated (humanPlayerId present)
  onWindowPoke = null,    // (actionId, kind, extra) => void
  whileAway = null,       // { newMsgs, newKeepsake, newWindow } on a real-gap return
  onDraw = null,          // opens the existing Sketchbook easel
}) => {
  const name = residentName || 'your resident';
  const [awayDismissed, setAwayDismissed] = useState(false);
  return (
    <div style={{ boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      {/* 0 · While you were out — return receipts, real rows only, dismissable */}
      {whileAway && !awayDismissed && (
        <Block style={{
          background: 'radial-gradient(220px 100px at 82% 0%, rgba(227,155,171,0.16), transparent 62%), linear-gradient(168deg, rgba(43,26,45,0.7), rgba(23,16,23,0.7))',
          border: '1px solid rgba(227,155,171,0.28)',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', color: '#F2C3CE', fontSize: '0.92em' }}>
              while you were out
            </div>
            <button
              onClick={() => setAwayDismissed(true)}
              aria-label="Dismiss"
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,238,216,0.5)', cursor: 'pointer', fontSize: '0.9em', minWidth: 30, minHeight: 30 }}
            >✕</button>
          </div>
          <div style={{ fontSize: '0.8em', color: 'rgba(255,238,216,0.82)', marginTop: 5, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {whileAway.newWindow && <div>🪟 a new window: <span style={{ fontStyle: 'italic' }}>{whileAway.newWindow}</span></div>}
            {whileAway.newKeepsake && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <img src={whileAway.newKeepsake.url} alt="new keepsake" loading="lazy"
                  style={{ width: 30, height: 30, borderRadius: 5, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.18)' }} />
                <span>something new on the shelf</span>
              </div>
            )}
            {whileAway.newMsgs > 0 && <div>💬 {whileAway.newMsgs} new message{whileAway.newMsgs === 1 ? '' : 's'} to scroll back to</div>}
          </div>
        </Block>
      )}

      {/* 1 · Presence + tempo — MERGED (Nina 08-29 masthead redesign): the
             masthead now owns the big hero (portrait + name + live status + day),
             so the old rail presence header [52px portrait + name + brass DAY
             plate] was a straight duplicate. Slimmed to a re-anchor — a 30px
             portrait DOT + the live status line — folded above the honest
             turn-clock (blocks 1+2 collapse into one "presence + tempo" card).
             The dot re-anchors the face for when the center-column hero scrolls
             out of view; the presence block's warm gradient/border rides here so
             the rail top stays warm. Never a live tick (the arc bans fake
             heartbeats). Name is owned by title+hero; day is the masthead coda. */}
      <Block style={{
        background: 'radial-gradient(260px 120px at 80% 0%, rgba(240,172,85,0.18), transparent 62%), linear-gradient(168deg, rgba(38,26,48,0.7), rgba(23,16,23,0.7))',
        border: '1px solid rgba(255,222,170,0.2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,222,170,0.5)',
            boxShadow: '0 0 12px rgba(240,172,85,0.3)',
            background: 'radial-gradient(circle at 36% 30%, rgba(255,222,168,0.85), rgba(240,172,85,0.28) 60%, rgba(20,14,20,0.6))',
          }}>
            {residentImage
              ? <img src={residentImage} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 15 }}>🏮</span>}
          </div>
          <div style={{ minWidth: 0, flex: 1, fontFamily: SERIF, fontSize: '0.88em', color: 'rgba(255,238,216,0.86)' }}>
            {residentStatus
              ? <><span style={{ color: '#E39BAB' }}>●</span> {residentStatus}</>
              : <span style={{ fontStyle: 'italic', color: 'rgba(255,238,216,0.55)' }}>here with you</span>}
          </div>
        </div>
        <div style={{
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '0.72em',
          marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,236,210,0.1)',
        }}>
          {/* 🗣️ 09-25 (Nina): no gameplay words here (turns, taps waiting,
              ticks read as a game); the "what X sees" Mind card is retired. */}
          <div style={{ color: 'rgba(255,238,214,0.8)' }}>
            {lastTurnAgo ? `last spoke ${lastTurnAgo}` : 'settling in'}
          </div>
        </div>
      </Block>

      {/* 3b · Windows the resident hung — collected out of the feed (one home).
             The clearest "the UI IS their speech" surface; close/add ride the
             same window_poke, reacted to next turn. */}
      {windows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '0.68em',
            letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,238,216,0.42)',
          }}>🪟 {windows.length === 1 ? 'a window they hung' : `${windows.length} windows they hung`}</div>
          {windows.map((w) => (
            <PortedWindowCard
              key={w.actionId}
              gameId={gameId}
              actionId={w.actionId}
              title={w.title}
              body={w.body}
              adds={windowAdds[w.actionId] || []}
              canInteract={canInteract}
              onPoke={onWindowPoke ? (kind, extra) => onWindowPoke(w.actionId, kind, { card_label: w.title, ...(extra || {}) }) : null}
            />
          ))}
        </div>
      )}

      {/* 4 · Keepsake shelf + the making loop */}
      <Block>
        <KeepsakeShelf actions={keepsakeActions} />
        {onDraw && (
          <button
            onClick={onDraw}
            style={{
              marginTop: 12, width: '100%', boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px 12px', minHeight: 40, borderRadius: 10, cursor: 'pointer',
              border: '1px solid rgba(240,172,85,0.45)', background: 'rgba(240,172,85,0.12)',
              color: '#F5C580', fontSize: '0.82em', fontWeight: 600,
            }}
          >✏️ draw {name} something</button>
        )}
      </Block>
    </div>
  );
};

export default PortedRail;
